import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { getApiBase } from "./utils/api";

// Import components
import Login from "./components/Login";
import Profile from "./components/dashboard/Profile";
import Dashboard from "./components/Dashboard";
import PublicLanding from "./components/PublicLanding";
import InviteSignup from "./components/InviteSignup";
import SubscriptionApprove from "./components/SubscriptionApprove";
import ExecutiveApprove from "./components/ExecutiveApprove";
import GoogleCallback from "./components/GoogleCallback";
import MicrosoftCallback from "./components/MicrosoftCallback";
import SAMLCallback from "./components/SAMLCallback";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SSEProvider } from "./contexts/SSEContext";
import { TutorialProvider, useTutorial } from "./contexts/TutorialContext";
import { ParadigmProvider } from "./contexts/ParadigmContext";
import { DashboardLayoutProvider } from "./contexts/DashboardLayoutContext";
import TutorialOverlay from "./components/tutorial/TutorialOverlay";

// Bridge component: reads paradigm from TutorialContext's entitlements
// and passes it to ParadigmProvider
function ParadigmBridge({ children }) {
  const { entitlements } = useTutorial();
  const paradigm = entitlements?.paradigm || 'ea_travel';
  return <ParadigmProvider paradigm={paradigm}>{children}</ParadigmProvider>;
}

function App() {
  // Some native date pickers (e.g. Safari) keep their calendar popover open
  // after a day is picked. Blur any date input once it holds a complete date
  // so the calendar closes everywhere in the app, current and future fields.
  useEffect(() => {
    const closeDatePicker = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT' && t.type === 'date' && t.value) {
        t.blur();
      }
    };
    document.addEventListener('change', closeDatePicker, true);
    return () => document.removeEventListener('change', closeDatePicker, true);
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <TutorialProvider>
          <ParadigmBridge>
          <SSEProvider>
            <div className="App min-h-screen bg-gray-50">
              <BrowserRouter>
                <DashboardLayoutProvider>
                <TutorialOverlay />
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/welcome" element={<PublicLanding />} />
                <Route path="/invite/:token" element={<InviteSignup />} />
                <Route path="/subscription/approve/:token" element={<SubscriptionApprove />} />
                <Route path="/executive/approve/:token" element={<ExecutiveApprove />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                <Route path="/auth/saml/callback" element={<SAMLCallback />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/dashboard/*" element={<DashboardRedirect />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                </Routes>
              </DashboardLayoutProvider>
              </BrowserRouter>
            </div>
          </SSEProvider>
          </ParadigmBridge>
        </TutorialProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

// Redirect /dashboard/* to /* (strip the /dashboard prefix)
function DashboardRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/dashboard/, '') || '/';
  return <Navigate to={newPath + location.search + location.hash} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isRoot = location.pathname === '/';

  // For unauthenticated visitors at the root URL, check whether a landing page
  // has been published and show it in place of redirecting to /login. Any
  // other protected path still redirects to /login.
  const [landingPublished, setLandingPublished] = useState(null);
  useEffect(() => {
    if (loading || user || !isRoot) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${getApiBase()}/landing/config`, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
        });
        if (!cancelled) {
          if (response.ok) {
            const cfg = await response.json();
            setLandingPublished(Boolean(cfg?.published));
          } else {
            setLandingPublished(false);
          }
        }
      } catch (err) {
        console.error('Failed to load landing config:', err);
        if (!cancelled) setLandingPublished(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, isRoot]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    if (isRoot) {
      if (landingPublished === null) {
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
        );
      }
      if (landingPublished) {
        return <PublicLanding />;
      }
    }
    return <Navigate to="/login" replace />;
  }

  // Check for stored returnUrl from auth flow (e.g., after OIDC/SSO login)
  // Only redirect if we're on root path to avoid redirect loops
  const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
  if (storedReturnUrl && isRoot) {
    sessionStorage.removeItem('authReturnUrl');
    return <Navigate to={storedReturnUrl} replace />;
  }

  return children;
}

export default App;